<template>
  <div
    v-if="!formHidden"
    class="onboarding-data-entry"
  >
    <v-card
      rounded="xl"
      elevation="0"
      :style="{ border: '1px solid rgba(var(--v-border-color), 0.12)' }"
      class="onboarding-data-entry__card"
    >
      <div
        v-if="showFormScrapeProgress"
        class="onboarding-data-entry__scrape-bar"
      >
        <ScrapeStatusBar
          :progress="formScrapeProgress"
          @dismiss="aiStore.dismissScrapeProgressPanel()"
        />
      </div>
      <div class="onboarding-data-entry__body pa-6 pa-md-8 pb-8 pb-md-10">
        <CompanyNameWebsiteSection />
        <ConnectionsSection />
        <AddressSection />
        <ContactPersonSection />
        <AmbassadorSection />
        <ProfileCopySection />
        <BrandColorsSection />
        <CompanyImagesSection
          :wizard-company="wizardCompany"
          @image-uploaded="emit('image-uploaded', $event)"
        />
        <LegalIntegrationsSection
          :wizard-company="wizardCompany"
          :legal-consents="legalConsents"
          @update:legal-consents="emit('update:legalConsents', $event)"
          @widget-integration="emit('widget-integration', $event)"
        />
        <div
          class="d-flex flex-wrap justify-space-between align-center ga-3 onboarding-enter-data-actions"
        >
          <v-btn
            variant="outlined"
            rounded="lg"
            @click="emit('back')"
          >
            {{ $t('onboarding.stepContainer.back') }}
          </v-btn>
          <v-btn
            color="primary"
            rounded="lg"
            size="large"
            class="onboarding-enter-data-actions__continue"
            @click="onContinue"
          >
            {{ continueLabel }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-alert
      v-if="!isAssistedOnboardingEnabled"
      type="info"
      variant="tonal"
      density="compact"
      rounded="lg"
      class="mt-4"
      icon="mdi-lightbulb-outline"
    >
      <span class="text-body-2">
        {{ $t('onboarding.wizard.data-entry.tip-manual') }}
      </span>
    </v-alert>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, nextTick, provide, reactive, toRef } from 'vue'

import type { LegalConsentsValues } from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import ScrapeStatusBar from '@/components/ai/onboarding/ScrapeStatusBar.vue'
import { buildScrapeProgressStartingView } from '@/lib/ai/scrapeProgress'

import AddressSection from './components/data-entry/AddressSection.vue'
import AmbassadorSection from './components/data-entry/AmbassadorSection.vue'
import BrandColorsSection from './components/data-entry/BrandColorsSection.vue'
import CompanyImagesSection from './components/data-entry/CompanyImagesSection.vue'
import CompanyNameWebsiteSection from './components/data-entry/CompanyNameWebsiteSection.vue'
import ConnectionsSection from './components/data-entry/ConnectionsSection.vue'
import ContactPersonSection from './components/data-entry/ContactPersonSection.vue'
import LegalIntegrationsSection from './components/data-entry/LegalIntegrationsSection.vue'
import ProfileCopySection from './components/data-entry/ProfileCopySection.vue'
import { onboardingDataEntryKey } from './composables/onboardingDataEntryContext'
import { useOnboardingFeatures } from './composables/useOnboardingFeatures'
import { useOnboardingFieldChrome } from './composables/useOnboardingFieldChrome'
import {
  type BrandColorField,
  getBrandColorIssue,
  getFirstBrandColorIssue,
  isBrandColorField,
} from './lib/brandColorValidation'
import type { OnboardingFormData } from './lib/onboardingPrefill'
import type { OnboardingWizardCompany } from './lib/onboardingWizardCompany'

export type { OnboardingFormData } from './lib/onboardingPrefill'
export type {
  OnboardingWizardCompany,
  OnboardingWizardCompanyImage,
} from './lib/onboardingWizardCompany'

const props = defineProps<{
  formData: OnboardingFormData
  aiCapturedFields: Set<string>
  /** Apollo `company` (avoid naming collision with `company` from `@/lib/features`). */
  wizardCompany?: OnboardingWizardCompany | null
  /**
   * When true the entire form is hidden and the user works through the chat assistant only.
   * Field focus scrolling is also suppressed.
   */
  formHidden?: boolean
  /** Legal-consent state, owned by the onboarding wizard (index.vue) and shared with the Review step. */
  legalConsents?: LegalConsentsValues | null
  /**
   * Shared, session-aware US-market flag from `useOnboardingForm` (index.vue). Recomputing it here
   * would drop the Genaisis `session.market` signal and desync field visibility from validation.
   */
  isUsOnboarding: boolean
  continueLabel: string
}>()

const emit = defineEmits<{
  'update:field': [field: string, value: string | boolean]
  'image-uploaded': [payload: { kind: 'logo' | 'team' | 'profile'; url?: string }]
  'widget-integration': [payload: { enabled: boolean }]
  'update:legalConsents': [value: LegalConsentsValues]
  back: []
  continue: []
}>()

const { isAssistedOnboardingEnabled, isOnboardingWebScrapingEnabled } = useOnboardingFeatures()

const { aiStore, fieldClass, onFieldFocus } = useOnboardingFieldChrome({
  formHidden: () => Boolean(props.formHidden),
})
const { scrapeRunning, scrapeProgressPanelView } = storeToRefs(aiStore)

const formScrapeProgress = computed(() => {
  if (!isOnboardingWebScrapingEnabled.value) return null
  if (isAssistedOnboardingEnabled.value) return null
  if (scrapeProgressPanelView.value) return scrapeProgressPanelView.value
  if (scrapeRunning.value) return buildScrapeProgressStartingView()
  return null
})

const showFormScrapeProgress = computed(() => Boolean(formScrapeProgress.value))

const updateField = (field: string, value: string | boolean) => {
  // Single choke point for manual edits: an inaccessible brand color is never emitted,
  // whichever section tries to write it.
  if (
    isBrandColorField(field) &&
    getBrandColorIssue(field, typeof value === 'string' ? value : '')
  ) {
    return
  }
  aiStore.dismissFieldAiSuggestion(field)
  emit('update:field', field, value)
}

// A typed color that fails stays in its field until it is corrected or canceled.
const brandColorDrafts = reactive<Record<BrandColorField, string | null>>({
  primaryColor: null,
  secondaryColor: null,
})

// Stored values can still fail (AI capture, older data), and so can a draft. Don't let the user
// continue with them; take them to the field instead, where BrandColorsSection shows the reason.
const brandColorIssue = computed(() =>
  getFirstBrandColorIssue({
    primaryColor: brandColorDrafts.primaryColor ?? props.formData.primaryColor,
    secondaryColor: brandColorDrafts.secondaryColor ?? props.formData.secondaryColor,
  })
)

const onContinue = async () => {
  const blocked = brandColorIssue.value
  if (!blocked) {
    emit('continue')
    return
  }
  await nextTick()
  const input = document.getElementById(`onboarding-field-${blocked.field}`)
  input?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  input?.focus({ preventScroll: true })
}

provide(onboardingDataEntryKey, {
  formData: toRef(props, 'formData'),
  aiCapturedFields: toRef(props, 'aiCapturedFields'),
  updateField,
  onFieldFocus,
  fieldClass,
  isUsOnboarding: toRef(props, 'isUsOnboarding'),
  brandColorDrafts,
})
</script>

<style scoped>
.onboarding-data-entry {
  width: 100%;
}

.onboarding-data-entry__scrape-bar {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface));
}

.onboarding-data-entry__scrape-bar :deep(.scrape-status-bar) {
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
}

.onboarding-enter-data-actions {
  position: relative;
  z-index: 1300;
  flex-shrink: 0;
  width: 100%;
  padding-top: 20px;
  padding-bottom: 4px;
}

/* Sit above the ProductFruits "Support & Tipps" launcher (z-index 1200). */
.onboarding-enter-data-actions__continue {
  position: relative;
  z-index: 1300;
}

:deep(.onboarding-field--ai-updated .v-field__outline) {
  --v-field-border-opacity: 1;
  color: rgb(var(--v-theme-primary));
}

:deep(.onboarding-field--ai-focus .v-field) {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.25);
}
</style>
